import { slices } from "@/constants/sliceNames";
import {
  AnalyticsDefectsInfo,
  AnalyticsGeneralInfo,
  AnalyticsJudgementInfo,
  JudgementPointsInfo,
  PlatesImagesInfo,
} from "@/entities/BatchPredictionMonitor/BatchPredictionMonitorAnalytics";
import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "@/axios/defaultAxios";
import { endpoints } from "@/axios/endpoints";
import { BatchPredictionMonitor } from "@/entities/BatchPredictionMonitor/BatchPredictionMonitor";
import { BatchPredictionMonitorData } from "@/entities/BatchPredictionMonitor/BatchPredictionMonitorData";
import { serializeQueryParams } from "@/utils/utils";
import { TaskTypes } from "@/constants/task-types";

import { errorMessage, successMessage } from "../../actions/messages.actions";

const ENDPOINT = "/scheduledExecution";

const MONITOR_DATA_ENDPOINT = "/hoya/v2/monitors/:id/data/side/:side";

const EXPORT_MONITOR_DATA_ENDPOINT = "/hoya/v2/monitors/:id/export";

/******* Get batch prediction monitors ********/

export const requestBatchPredictionMonitors = createAsyncThunk(
  `${slices.batchPredictionMonitoring}/requestBatchPredictionMonitors`,
  async (params, { rejectWithValue, dispatch }) => {
    if (!params) {
      params = {};
    }
    params["exclude_items"] = "true";

    try {
      const response = await axios.get(`${ENDPOINT}?${serializeQueryParams(params)}`);
      // process data
      const { data, ...rest } = response.data.data;
      const monitors = data.map(BatchPredictionMonitor.fromApi);
      return { monitors, ...rest };
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error ?? error.message));
      return rejectWithValue(error.response?.data?.error ?? error.message);
    }
  }
);

export const requestBatchPredictionMonitorsStatus = {
  [requestBatchPredictionMonitors.fulfilled]: (state, action) => {
    const { monitors, records_count, page_limit, current_page, total_pages } = action.payload;
    state.loading = false;
    state.monitors = monitors;
    state.recordsCount = records_count;
    state.pageLimit = page_limit;
    state.currentPage = current_page;
    state.totalPages = total_pages;
  },
  [requestBatchPredictionMonitors.pending]: (state, action) => {
    state.loading = true;
  },
  [requestBatchPredictionMonitors.rejected]: (state, action) => {
    state.loading = false;
    state.monitors = [];
    state.recordsCount = 0;
  },
};

/******* Get single batch prediction monitor by monitor ID ********/

export const requestSingleBatchPredictionMonitor = createAsyncThunk(
  `${slices.batchPredictionMonitoring}/requestSingleBatchPredictionMonitor`,
  async (monitorId, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.get(`${ENDPOINT}/${monitorId}`);
      const monitor = BatchPredictionMonitor.fromApi(response.data.data);
      return monitor;
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error ?? error.message));
      return rejectWithValue(error.response?.data?.error ?? error.message);
    }
  }
);

export const requestSingleBatchPredictionMonitorStatus = {
  [requestSingleBatchPredictionMonitor.fulfilled]: (state, action) => {
    state.loading = false;
    state.item = action.payload;
  },
  [requestSingleBatchPredictionMonitor.pending]: (state, action) => {
    state.loading = true;
  },
  [requestSingleBatchPredictionMonitor.rejected]: (state, action) => {
    state.loading = false;
    state.item = {};
  },
};

/******* Get batch prediction monitor data by monitor ID ********/

export const requestBatchPredictionMonitorData = createAsyncThunk(
  `${slices.batchPredictionMonitoring}/requestBatchPredictionMonitorData`,
  async ({ monitorId, params }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.get(
        `${MONITOR_DATA_ENDPOINT.replace(":id", monitorId).replace(":side", params["side"])}?${serializeQueryParams(
          params
        )}`
      );
      const { data, ...rest } = response.data.data;
      const monitorData = BatchPredictionMonitorData.fromApi(data);
      return { monitorData, ...rest };
    } catch (error) {
      if (error.response?.status !== 404) {
        dispatch(errorMessage(error.response?.data?.error ?? error.message));
      }
      return rejectWithValue(error.response?.data?.error ?? error.message);
    }
  }
);

export const requestBatchPredictionMonitorDataStatus = {
  [requestBatchPredictionMonitorData.fulfilled]: (state, action) => {
    const { monitorData, current_page, records_count } = action.payload;
    state.dataLoading = false;
    state.itemData =
      current_page === 1
        ? monitorData
        : new BatchPredictionMonitorData(
            monitorData.getHeader(),
            [...state.itemData.rows, ...monitorData.rows],
            [...state.itemData.getNames(), ...monitorData.getNames()]
          );
    state.totalItemData = records_count;
  },
  [requestBatchPredictionMonitorData.pending]: (state, action) => {
    state.dataLoading = true;
  },
  [requestBatchPredictionMonitorData.rejected]: (state, action) => {
    state.dataLoading = false;
    state.itemData = new BatchPredictionMonitorData();
    state.totalItemData = 1;
  },
};

/******* Export batch prediction monitor by monitor ID ********/

export const requestBatchPredictionMonitorDataForExport = (monitorId, params) => {
  const url = `${EXPORT_MONITOR_DATA_ENDPOINT.replace(":id", monitorId)}?${serializeQueryParams(params)}`;
  return (dispatch) => {
    return axios
      .get(url)
      .then((res) => res.data.data)
      .catch((err) => {
        dispatch(errorMessage(err.response?.data?.error ?? err.message));
      });
  };
};

/******* Get feedback projects list by dataset ID ********/

export const requestFeedbackProjects = createAsyncThunk(
  `${slices.batchPredictionMonitoring}/requestFeedbackProjects`,
  async ({ dataId, type }, { rejectWithValue, dispatch }) => {
    try {
      const url = type === TaskTypes.OBJECT_DETECTION ? endpoints.feedbackProjectsMonitors : endpoints.feedbackProjects;
      const response = await axios.get(`${url.replace(":id", dataId)}`);
      return response.data.data;
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error ?? error.message));
      return rejectWithValue(error.response?.data?.error ?? error.message);
    }
  }
);

export const requestFeedbackProjectsStatus = {
  [requestFeedbackProjects.fulfilled]: (state, action) => {
    state.loading = false;
    state.feedback.projects = action.payload;
  },
  [requestFeedbackProjects.pending]: (state, action) => {
    state.loading = true;
  },
  [requestFeedbackProjects.rejected]: (state, action) => {
    state.loading = false;
    state.feedback.projects = [];
  },
};

/******* Create feedback project ********/

export const requestCreateFeedbackProject = createAsyncThunk(
  `${slices.batchPredictionMonitoring}/requestCreateFeedbackProject`,
  async ({ dataId, projectData, type }, { rejectWithValue, dispatch }) => {
    try {
      const url = type === TaskTypes.OBJECT_DETECTION ? endpoints.feedbackProjectsMonitors : endpoints.feedbackProjects;
      const response = await axios.post(`${url.replace(":id", dataId)}`, projectData);
      dispatch(successMessage(response.data.message));
      const { data } = response.data;
      return {
        project_name: projectData.project_name,
        data,
      };
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error ?? error.message));
      return rejectWithValue(error.response?.data?.error ?? error.message);
    }
  }
);

export const requestCreateFeedbackProjectStatus = {
  [requestCreateFeedbackProject.fulfilled]: (state, action) => {
    state.loading = false;
  },
  [requestCreateFeedbackProject.pending]: (state, action) => {
    state.loading = true;
  },
  [requestCreateFeedbackProject.rejected]: (state, action) => {
    state.loading = false;
  },
};

/******* Push feedback for an image ********/

export const requestPushImageFeedback = createAsyncThunk(
  `${slices.batchPredictionMonitoring}/requestPushImageFeedback`,
  async ({ dataId, projectId, feedbackData, type }, { rejectWithValue, dispatch }) => {
    try {
      const url =
        type === TaskTypes.OBJECT_DETECTION
          ? endpoints.feedbackProjectsMonitors + "/push_image"
          : endpoints.pushFeedbackImg.replace(":id", dataId).replace(":project_id", projectId);
      const response = await axios.post(`${url.replace(":id", dataId)}`, feedbackData);
      return response.data.data;
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error ?? error.message));
      return rejectWithValue(error.response?.data?.error ?? error.message);
    }
  }
);

export const requestPushImageFeedbackStatus = {
  [requestPushImageFeedback.fulfilled]: (state, action) => {
    state.loading = false;
  },
  [requestPushImageFeedback.pending]: (state, action) => {
    state.loading = true;
  },
  [requestPushImageFeedback.rejected]: (state, action) => {
    state.loading = false;
  },
};

/** Get monitor analytics general info */
export const requestMonitorAnalyticsGeneralInfo = createAsyncThunk(
  `${slices.batchPredictionMonitoring}/requestMonitorAnalyticsGeneralInfo`,
  async ({ endpoints, monitorId, platesIds, filters }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.get(
        `${endpoints.generalInfo.replace(":id", monitorId)}?plates_ids=${platesIds.join(",")}&from=${filters.from}&to=${
          filters.to
        }`
      );
      return response.data.data;
    } catch (error) {
      if (error.response?.status !== 400) {
        dispatch(errorMessage(error.response?.data?.error ?? error.message));
      }
      return rejectWithValue(error.response?.data?.error ?? error.message);
    }
  }
);

export const requestMonitorAnalyticsGeneralInfoStatus = {
  [requestMonitorAnalyticsGeneralInfo.fulfilled]: (state, action) => {
    state.generalInfo = AnalyticsGeneralInfo.fromApi(action.payload);
    state.analysisError = "";
  },
  [requestMonitorAnalyticsGeneralInfo.pending]: (state, action) => {
    state.generalInfo.loading = true;
    state.analysisError = "";
  },
  [requestMonitorAnalyticsGeneralInfo.rejected]: (state, action) => {
    state.analysisError = action.payload;
  },
};

/** Get judgments per side analytics */
export const requestMonitorAnalyticsJudgmentsPerSide = createAsyncThunk(
  `${slices.batchPredictionMonitoring}/requestMonitorAnalyticsJudgmentsPerSide`,
  async ({ endpoints, monitorId, platesIds, filters }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.get(
        `${endpoints.judgementPerSide.replace(":id", monitorId)}?plates_ids=${platesIds.join(",")}&from=${
          filters.from
        }&to=${filters.to}`
      );
      return response.data.data;
    } catch (error) {
      if (error.response?.status !== 400) {
        dispatch(errorMessage(error.response?.data?.error ?? error.message));
      }
      return rejectWithValue(error.response?.data?.error ?? error.message);
    }
  }
);

export const requestMonitorAnalyticsJudgmentsPerSideStatus = {
  [requestMonitorAnalyticsJudgmentsPerSide.fulfilled]: (state, action) => {
    state.judgementInfo = new AnalyticsJudgementInfo(action.payload);
  },
  [requestMonitorAnalyticsJudgmentsPerSide.pending]: (state, action) => {
    state.judgementInfo.loading = true;
  },
  [requestMonitorAnalyticsJudgmentsPerSide.rejected]: (state, action) => {
    state.judgementInfo = new AnalyticsJudgementInfo();
  },
};

/** Get defects analysis per side */
export const requestMonitorAnalyticsDefectsPerSide = createAsyncThunk(
  `${slices.batchPredictionMonitoring}/requestMonitorAnalyticsDefectsPerSide`,
  async ({ endpoints, monitorId, platesIds, filters }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.get(
        `${endpoints.labelsPerSide.replace(":id", monitorId)}?plates_ids=${platesIds.join(",")}&from=${
          filters.from
        }&to=${filters.to}`
      );
      return response.data.data;
    } catch (error) {
      if (error.response?.status !== 400) {
        dispatch(errorMessage(error.response?.data?.error ?? error.message));
      }
      return rejectWithValue(error.response?.data?.error ?? error.message);
    }
  }
);

export const requestMonitorAnalyticsDefectsPerSideStatus = {
  [requestMonitorAnalyticsDefectsPerSide.fulfilled]: (state, action) => {
    state.defectsInfo = new AnalyticsDefectsInfo(action.payload);
  },
  [requestMonitorAnalyticsDefectsPerSide.pending]: (state, action) => {
    state.defectsInfo.loading = true;
  },
  [requestMonitorAnalyticsDefectsPerSide.rejected]: (state, action) => {
    state.defectsInfo = new AnalyticsDefectsInfo();
  },
};

export const requestDefectsLocationsPerSide = createAsyncThunk(
  `${slices.batchPredictionMonitoring}/requestMonitorAnalyticsDefectsLocationsPerSide`,
  async ({ endpoints, monitorId, platesIds, filters }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.get(
        `${endpoints.defectLocations.replace(":id", monitorId)}?plates_ids=${platesIds.join(",")}&from=${
          filters.from
        }&to=${filters.to}`
      );
      return response.data.data;
    } catch (error) {
      if (error.response?.status !== 400) {
        dispatch(errorMessage(error.response?.data?.error ?? error.message));
      }
      return rejectWithValue(error.response?.data?.error ?? error.message);
    }
  }
);

export const requestDefectsLocationsPerSideStatus = {
  [requestDefectsLocationsPerSide.fulfilled]: (state, action) => {
    state.judgementPointsInfo = new JudgementPointsInfo(action.payload);
  },
  [requestDefectsLocationsPerSide.pending]: (state, action) => {
    state.judgementPointsInfo = new JudgementPointsInfo(action.payload);
  },
  [requestDefectsLocationsPerSide.rejected]: (state, action) => {
    state.judgementPointsInfo = new JudgementPointsInfo();
  },
};

export const requestPlateImagesAnalytics = createAsyncThunk(
  `${slices.batchPredictionMonitoring}/requestMonitorPlateImagesAnalytics`,
  async ({ endpoints, monitorId, platesIds, filters }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.get(
        `${endpoints.judgementPerImage.replace(":id", monitorId)}?plates_ids=${platesIds.join(",")}&from=${
          filters.from
        }&to=${filters.to}&side=${filters.side}`
      );
      return response.data.data;
    } catch (error) {
      if (error.response?.status !== 400) {
        dispatch(errorMessage(error.response?.data?.error ?? error.message));
      }
      return rejectWithValue(error.response?.data?.error ?? error.message);
    }
  }
);

export const requestPlateImagesAnalyticsStatus = {
  [requestPlateImagesAnalytics.fulfilled]: (state, action) => {
    state.plateImagesInfo = new PlatesImagesInfo(action.payload);
    state.plateImagesInfo.loading = false;
  },
  [requestPlateImagesAnalytics.pending]: (state, action) => {
    state.plateImagesInfo = new PlatesImagesInfo({}, true);
  },
  [requestPlateImagesAnalytics.rejected]: (state, action) => {
    state.plateImagesInfo = new PlatesImagesInfo();
  },
};
